import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Building2, Users, BarChart2, CreditCard } from 'lucide-react';
import { superAdminAPI } from '../../lib/api';
import toast from 'react-hot-toast';

const PLAN_COLORS = { basic: '#6B7280', pro: '#3B82F6', enterprise: '#8B5CF6' };

const TenantDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [planUpdate, setPlanUpdate] = useState({ plan: '', billingCycle: 'monthly' });
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    superAdminAPI.getTenant(id)
      .then(res => {
        setTenant(res.data.data);
        setPlanUpdate({ plan: res.data.data.subscription?.plan || 'basic', billingCycle: 'monthly' });
      })
      .catch(() => toast.error('Failed to load tenant'))
      .finally(() => setLoading(false));
  }, [id]);

  const handlePlanUpdate = async (e) => {
    e.preventDefault();
    setUpdating(true);
    try {
      await superAdminAPI.updateSubscription(id, planUpdate);
      toast.success('Subscription updated');
      const res = await superAdminAPI.getTenant(id);
      setTenant(res.data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally {
      setUpdating(false);
    }
  };

  const handleSuspend = async () => {
    const isSuspended = tenant?.subscription?.status === 'suspended';
    if (!window.confirm(`${isSuspended ? 'Unsuspend' : 'Suspend'} this tenant?`)) return;
    try {
      await superAdminAPI.suspendTenant(id, isSuspended ? 'Reactivated by super admin' : 'Suspended by super admin');
      toast.success(`Tenant ${isSuspended ? 'unsuspended' : 'suspended'}`);
      const res = await superAdminAPI.getTenant(id);
      setTenant(res.data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Action failed');
    }
  };

  if (loading) return <div className="loading-center"><div className="spinner spinner-lg" /></div>;
  if (!tenant) return <div className="empty-state"><h3>Tenant not found</h3></div>;

  const isSuspended = tenant.subscription?.status === 'suspended';

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost btn-icon" onClick={() => navigate('/super-admin/tenants')}><ArrowLeft size={20} /></button>
          <div>
            <h1 className="page-title">{tenant.name}</h1>
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <span className="badge" style={{ color: PLAN_COLORS[tenant.subscription?.plan], background: `${PLAN_COLORS[tenant.subscription?.plan]}15` }}>
                {tenant.subscription?.plan}
              </span>
              <span className={`badge ${isSuspended ? 'badge-red' : tenant.subscription?.status === 'active' ? 'badge-green' : 'badge-yellow'}`}>
                {tenant.subscription?.status}
              </span>
            </div>
          </div>
        </div>
        <button className={`btn ${isSuspended ? 'btn-success' : 'btn-danger'}`} onClick={handleSuspend}>
          {isSuspended ? 'Unsuspend Tenant' : 'Suspend Tenant'}
        </button>
      </div>

      <div className="tabs">
        {['overview', 'subscription', 'usage'].map(t => (
          <button key={t} className={`tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)} style={{ textTransform: 'capitalize' }}>{t}</button>
        ))}
      </div>

      {tab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="card">
              <div className="card-header"><span className="card-title">Company Information</span></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                {[
                  ['Company Name', tenant.name],
                  ['Email', tenant.email],
                  ['Phone', tenant.phone || '—'],
                  ['Website', tenant.website || '—'],
                  ['Subdomain', tenant.subdomain],
                  ['Created', tenant.createdAt ? new Date(tenant.createdAt).toLocaleDateString() : '—'],
                ].map(([k, v]) => (
                  <div key={k}>
                    <div style={{ fontSize: 11, color: '#9CA3AF', textTransform: 'uppercase', marginBottom: 3 }}>{k}</div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: '#111827' }}>{v}</div>
                  </div>
                ))}
              </div>
              {tenant.address?.city && (
                <div style={{ marginTop: 14, padding: '10px 12px', background: '#F9FAFB', borderRadius: 8, fontSize: 13, color: '#6B7280' }}>
                  📍 {[tenant.address.street, tenant.address.city, tenant.address.state, tenant.address.country].filter(Boolean).join(', ')}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="card">
              <div className="card-header"><span className="card-title">Usage Summary</span></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[
                  { label: 'Properties', used: tenant.usage?.propertiesCount || 0, max: tenant.limits?.maxProperties, icon: Building2, color: '#3B82F6' },
                  { label: 'Users', used: tenant.usage?.usersCount || 0, max: tenant.limits?.maxUsers, icon: Users, color: '#8B5CF6' },
                  { label: 'Bookings (mo)', used: tenant.usage?.bookingsThisMonth || 0, max: null, icon: BarChart2, color: '#10B981' },
                ].map(({ label, used, max, icon: Icon, color }) => (
                  <div key={label}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#374151' }}>
                        <Icon size={13} color={color} />{label}
                      </span>
                      <span style={{ fontWeight: 600 }}>{used}{max ? ` / ${max === 999 ? '∞' : max}` : ''}</span>
                    </div>
                    {max && (
                      <div style={{ height: 5, background: '#E5E7EB', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ width: `${Math.min((used / (max === 999 ? used + 1 : max)) * 100, 100)}%`, height: '100%', background: color, borderRadius: 3 }} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="card">
              <div className="card-header"><span className="card-title">Limits</span></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
                {[
                  ['Calendar Sync', tenant.subscription?.features?.calendarSync],
                  ['CRM Module', tenant.subscription?.features?.crmModule],
                  ['Equipment Module', tenant.subscription?.features?.equipmentModule],
                  ['White-Label', tenant.subscription?.features?.whiteLabel],
                  ['API Access', tenant.subscription?.features?.apiAccess],
                  ['Priority Support', tenant.subscription?.features?.prioritySupport],
                ].map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6B7280' }}>{k}</span>
                    <span>{v ? '✅' : '❌'}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'subscription' && (
        <div style={{ maxWidth: 520 }}>
          <div className="card">
            <div className="card-header">
              <span className="card-title"><CreditCard size={15} style={{ display: 'inline', marginRight: 6 }} />Update Subscription</span>
            </div>
            <form onSubmit={handlePlanUpdate} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="form-group">
                <label className="form-label">Plan</label>
                <select className="form-select" value={planUpdate.plan} onChange={e => setPlanUpdate(p => ({ ...p, plan: e.target.value }))}>
                  <option value="basic">Basic — $29/mo</option>
                  <option value="pro">Pro — $79/mo</option>
                  <option value="enterprise">Enterprise — $199/mo</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Billing Cycle</label>
                <select className="form-select" value={planUpdate.billingCycle} onChange={e => setPlanUpdate(p => ({ ...p, billingCycle: e.target.value }))}>
                  <option value="monthly">Monthly</option>
                  <option value="annual">Annual (17% off)</option>
                </select>
              </div>
              <button type="submit" className="btn btn-primary" disabled={updating}>
                {updating ? 'Updating...' : 'Update Plan'}
              </button>
            </form>

            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #F3F4F6' }}>
              <div style={{ fontSize: 13, color: '#9CA3AF', marginBottom: 10 }}>Current Subscription Details</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14 }}>
                {[
                  ['Plan', tenant.subscription?.plan],
                  ['Status', tenant.subscription?.status],
                  ['Stripe Customer', tenant.subscription?.stripeCustomerId || '—'],
                  ['Stripe Subscription', tenant.subscription?.stripeSubscriptionId || '—'],
                  ['Period End', tenant.subscription?.currentPeriodEnd ? new Date(tenant.subscription.currentPeriodEnd).toLocaleDateString() : '—'],
                  ['Trial Ends', tenant.subscription?.trialEndsAt ? new Date(tenant.subscription.trialEndsAt).toLocaleDateString() : '—'],
                ].map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#9CA3AF' }}>{k}</span>
                    <span style={{ fontWeight: 500, textTransform: 'capitalize' }}>{v || '—'}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'usage' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
          {[
            { label: 'Total Properties', value: tenant.usage?.propertiesCount || 0, icon: Building2, color: '#3B82F6', bg: '#EFF6FF' },
            { label: 'Total Users', value: tenant.usage?.usersCount || 0, icon: Users, color: '#8B5CF6', bg: '#F5F3FF' },
            { label: 'Monthly Bookings', value: tenant.usage?.bookingsThisMonth || 0, icon: BarChart2, color: '#10B981', bg: '#ECFDF5' },
            { label: 'Monthly Revenue', value: `$${(tenant.usage?.revenueThisMonth || 0).toLocaleString()}`, icon: CreditCard, color: '#F59E0B', bg: '#FFFBEB' },
          ].map(({ label, value, icon: Icon, color, bg }) => (
            <div className="stat-card" key={label}>
              <div className="stat-icon" style={{ background: bg }}><Icon size={22} color={color} /></div>
              <div><div className="stat-value">{value}</div><div className="stat-label">{label}</div></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TenantDetailPage;
